import type { ButtonHTMLAttributes, HTMLAttributes } from "react";

import { cn } from "../cn";

/**
 * A filter pill or a read-only fact.
 *
 * Distinct from `Chip`: a chip reports a **state** in a semantic colour, a pill
 * carries a **fact or a filter** and is neutral until selected. Selected uses
 * the accent, which is legitimate — a filter is an action, not a state.
 */
const BASE = cn(
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-[var(--radius-pill)]",
  "border px-[11px] text-[12px]",
);

const OFF = "border-line bg-surface-2 font-medium text-dim";
const ON = "border-acc/35 bg-acc-sub font-semibold text-acc";

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  /** Selected state, for filters. */
  on?: boolean;
}

export function Pill({ on = false, className, ...rest }: PillProps) {
  return <span className={cn(BASE, on ? ON : OFF, className)} {...rest} />;
}

export interface FilterPillProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  on?: boolean;
}

/** The interactive variant. A filter you can click is a `<button>`, so it is
    reachable by keyboard and announces its state — the class-based version was
    a `<span>` with an onClick, which neither. */
export function FilterPill({ on = false, className, ...rest }: FilterPillProps) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={cn(BASE, on ? ON : OFF, "transition-colors hover:text-ink", className)}
      {...rest}
    />
  );
}
